import React from 'react';
import { Users, GraduationCap } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';

const Students = () => {
  const phdStudents = [
    { name: 'S. Sai Kumar', topic: 'Studies on Strain Softening Materials using Nonlocal FEM and EFG Methods', status: 'Completed', coGuide: '-' },
    { name: 'Pran Jyoti Saikia', topic: 'Experimental and Computational Analysis of Interface Fracture using CZM', status: 'Completed', coGuide: '-' },
    { name: 'Avneesh Kumar', topic: 'Advancing Understanding of Sandwich Structures through Mechanical Behaviour Modelling and Failure Analysis', status: 'Completed', coGuide: 'Prof. R. Ganesh Narayanan' },
    { name: 'P. Krishna Murthy', topic: 'Damage in Hyperelastic materials', status: 'Synopsis Seminar', coGuide: 'Prof. T.D. Pallicity' },
    { name: 'Lalit Kumar', topic: 'Structural Response and Failure Mechanisms of 3D-Printed Concrete from Green to Solid State', status: 'Completed', coGuide: 'Prof. B. Panda' },
    { name: 'M. Jyoti Baishya', topic: 'Design and Development of Ultrastiff High-strength Metamaterials', status: 'Completed', coGuide: 'Prof. P. Khanikar' },
    { name: 'V. Sathesh Raja', topic: 'Development of Continuously Reinforced Metal Matrix Composite through Wire Arc Additive Manufacturing', status: 'Completed', coGuide: 'Prof. S. Kapil' },
    { name: 'Sachin Kuriachan', topic: 'Recycling of FRP materials', status: 'Ongoing', coGuide: 'Prof. P. Tiwari' },
    { name: 'P. Rakesh', topic: 'Fracture and Fatigue of natural fiber composites', status: 'Ongoing', coGuide: 'Prof. U.K. Komal' },
    { name: 'Joy Narayan Chakraborty', topic: 'Design and Development of Prostatic retraction implant', status: 'Ongoing', coGuide: 'Prof. S. Kanagaraj' },
    { name: 'P. Vicky Kumar', topic: 'Additive Manufacturing of High-Performance PLA/MgTiO3 TPMS scaffolds for Bone Tissue Engineering', status: 'Completed', coGuide: 'Dr. A. Birru (NIT Manipur)' },
    { name: 'M. U. Gokul Krishnan', topic: 'Fracture Analysis of Porous Plates', status: 'Ongoing', coGuide: 'Dr. K.V.N. Surendra' },
    { name: 'Phul Babu', topic: 'Hybrid joining of 3D printed sandwich structures', status: 'Ongoing', coGuide: 'Prof. R. Ganesh Narayanan' },
    { name: 'Aashish EV', topic: 'Metasandwich structures', status: 'Ongoing', coGuide: 'Prof. S. Kanmani Subbu' },
    { name: 'S. P. Bhagath', topic: 'Mechanics of Metasandwich structures', status: 'Ongoing', coGuide: '-' },
  ];

  // Sort PhD students: Ongoing first, then Completed (no specific year sorting for PhD)
  const sortedPhdStudents = [...phdStudents].sort((a, b) => {
    if (a.status === 'Ongoing' && b.status !== 'Ongoing') return -1;
    if (a.status !== 'Ongoing' && b.status === 'Ongoing') return 1;
    return 0;
  });

  const mtechStudents = [
    { name: 'Sahil Narwal', topic: 'Design and development of aortic heart valve', year: '2018' },
    { name: 'Nikhil Kumar Singh', topic: 'Design and Development of Modular Reciprocating Gait Orthosis', year: '2019' },
    { name: 'Abhay Verma', topic: 'Manufacturing and Testing of Flax fiber composites', year: '2020' },
    { name: 'Mudit Kumar', topic: 'Delamination testing of FRP-Steel joints', year: '2020' },
    { name: 'Paladugu Rakesh', topic: 'Polymer composites - Manufacturing, Testing & Probabilistic failure', year: '2021' },
    { name: 'Y. Sasibhushan', topic: 'Development of Modular RGO and VSD for Rehabilitation', year: '2021' },
    { name: 'Nitinkumar B. Gaikwad', topic: 'Optimization of Fracture Parameters using ML algorithms', year: '2021' },
    { name: 'Krishna Kumar', topic: 'Data Logger for Underwater Device Systems', year: '2022' },
    { name: 'Fule Ankit Ashok Rao', topic: 'Global-local crack modelling using numerical methods', year: '2022' },
    { name: 'Ashish Kumar', topic: 'Hyper-reduction techniques for large-scale FE models', year: '2023' },
    { name: 'Deepansu Bisht', topic: 'Nonlocal damage in 3D printed concrete', year: '2023' },
    { name: 'Albert Shaji', topic: 'Alleviating Volumetric Locking in Hyperelastic Materials', year: '2023' },
    { name: 'Jasbir Singh', topic: 'Design and Manufacturing of Sandwich Composites', year: '2024' },
    { name: 'Neeraj Chauhan', topic: 'Design and manufacturing of Manpack Bridges', year: '2024' },
    { name: 'Sree Sandeep', topic: 'Auxetic Structures', year: '2024' },
    { name: 'Balumuri Tarakeshwara Rao', topic: 'Design and Manufacture of FRP-Steel hybrid composites', year: '2024' },
    { name: 'Tharani. V', topic: 'Numerical Modelling of Metasandwich Structures', year: '2024' },
    { name: 'Naveen Seervi', topic: 'Manufacturing and Testing of 3D-Printed PCL Scaffolds', year: '2025' },
    { name: 'Jaya Krishna Vankayalapati', topic: 'Equivalent Orthotropic Properties of Meta-sandwich Structures', year: '2025' },
    { name: 'Pranjit Boruah', topic: 'Impact behaviour of Metasandwich Structure', year: 'Ongoing' },
    { name: 'C. Vamsi Krishna', topic: 'Impact behaviour of Metasandwich Structure', year: 'Ongoing' },
  ];

  // Sort M.Tech students by year (newest first)
  const sortedMtechStudents = [...mtechStudents].sort((a, b) => {
    const yearA = a.year === 'Ongoing' ? 9999 : parseInt(a.year);
    const yearB = b.year === 'Ongoing' ? 9999 : parseInt(b.year);
    return yearB - yearA;
  });

  const btechStudents = [
    { students: 'Ujwal Gajbe & Jitender Prakash', topic: 'Atomistic Scale Simulation Of Crack Propagation', year: '2019' },
    { students: 'Anmol Deep, Dhiraj Mittal & Nitul Deori', topic: 'Failure of laminated composite materials - probabilistic analysis', year: '2020' },
    { students: 'Akshay Shah & Amit Rai', topic: 'Autonomous and Electric Vehicles – Simulation and ML', year: '2022' },
    { students: 'Bharat Bhushan Rai & B. Kiran Kumar', topic: 'Synthetic Enriched Function for Modelling Cracked Geometries', year: '2023' },
    { students: 'Janni Devi Sri Vara Prasad & Vedakanth', topic: 'Design and Meta-Sandwich Structure', year: '2025' },
  ];

  // Sort B.Tech students by year (newest first)
  const sortedBtechStudents = [...btechStudents].sort((a, b) => parseInt(b.year) - parseInt(a.year));

  return (
    <section id="students" className="section-container bg-slate-50">
      <h2 className="section-title">Student Supervision</h2>
      
      <div className="w-full">
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card className="text-center border-2 border-slate-200">
            <CardContent className="p-6">
              <GraduationCap size={40} className="text-[#1e3a8a] mx-auto mb-3" />
              <div className="text-4xl font-bold text-[#1e3a8a] mb-2">7</div>
              <div className="text-[#64748b] font-medium">PhD Students Supervised</div>
            </CardContent>
          </Card>
          <Card className="text-center border-2 border-slate-200">
            <CardContent className="p-6">
              <Users size={40} className="text-[#1e3a8a] mx-auto mb-3" />
              <div className="text-4xl font-bold text-[#1e3a8a] mb-2">6</div>
              <div className="text-[#64748b] font-medium">Research Scholars (Supervising)</div>
            </CardContent>
          </Card>
          <Card className="text-center border-2 border-slate-200">
            <CardContent className="p-6">
              <GraduationCap size={40} className="text-[#1e3a8a] mx-auto mb-3" />
              <div className="text-4xl font-bold text-[#1e3a8a] mb-2">12</div>
              <div className="text-[#64748b] font-medium">B.Tech Projects</div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="phd" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8">
            <TabsTrigger value="phd">PhD Students</TabsTrigger>
            <TabsTrigger value="mtech">M.Tech Students</TabsTrigger>
            <TabsTrigger value="btech">B.Tech Projects</TabsTrigger>
          </TabsList>

          <TabsContent value="phd">
            <div className="grid gap-4">
              {sortedPhdStudents.map((student, index) => (
                <Card key={index} className="hover-lift">
                  <CardHeader>
                    <CardTitle className="flex items-start justify-between gap-4">
                      <span className="text-xl text-[#1e3a8a]">{student.name}</span>
                      <Badge
                        variant={student.status === 'Completed' ? 'secondary' : 'default'}
                        className={student.status === 'Ongoing' ? 'bg-green-100 text-green-800' : student.status === 'Synopsis Seminar' ? 'bg-blue-100 text-blue-800' : ''}
                      >
                        {student.status}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[#64748b] mb-2">
                      <span className="font-semibold text-[#475569]">Research Topic:</span> {student.topic}
                    </p>
                    {student.coGuide !== '-' && (
                      <p className="text-[#64748b]">
                        <span className="font-semibold text-[#475569]">Co-Guide:</span> {student.coGuide}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="mtech">
            <div className="grid gap-4">
              {sortedMtechStudents.map((student, index) => (
                <Card key={index} className="hover-lift">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <GraduationCap size={24} className="text-[#1e3a8a]" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-[#1e3a8a] mb-2">{student.name}</h4>
                        <p className="text-[#64748b] mb-1">{student.topic}</p>
                        <p className="text-sm text-[#64748b]">Completed: {student.year}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="btech">
            <div className="grid gap-4">
              {sortedBtechStudents.map((project, index) => (
                <Card key={index} className="hover-lift">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <Users size={24} className="text-[#1e3a8a]" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-[#1e3a8a] mb-2">{project.students}</h4>
                        <p className="text-[#64748b] mb-1">{project.topic}</p>
                        <p className="text-sm text-[#64748b]">Completed: {project.year}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default Students;